import { useEffect, useRef } from "react";
import { signal, effect } from "@preact/signals-react";
import { useQuery } from "@tanstack/react-query";
import { format, subHours } from "date-fns";
import { es } from "date-fns/locale";

import "./style.css";
import { cn } from "tools/ui";
import ImagePerfil from "./components/ImagePerfil";
import { IdentificationIcon, UserIcon } from "components/Icons";
import imgBackground from "assets/apps/background-meal.svg";
import { TimeIcon } from "components/Icons/linear";
import proxy from "api/proxy";
import Time from "components/Icons/linear/Time";

const currentTime = signal("");
const mealType = signal([]);
const currentMeal = signal(null);
const today = new Date().toISOString().slice(0, 10);

function timeToMinutes(time) {
  const [h, m, s] = time.split(":").map(Number);
  return h * 60 + m + (s || 0) / 60;
}

function maskLastFour(value) {
  if (!value) return "";

  const str = String(value);
  const visible = str.slice(-4);
  const masked = "*".repeat(Math.max(0, str.length - 4));

  return masked + visible;
}

function binaryToBlobUrl(base64, mime = "image/jpeg") {
  if (!base64) return null;
  try {
    const byteChars = atob(base64);
    const byteArrays = [];
    for (let offset = 0; offset < byteChars.length; offset += 512) {
      const slice = byteChars.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++)
        byteNumbers[i] = slice.charCodeAt(i);
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    const blob = new Blob(byteArrays, { type: mime });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

function getCurrentMealType(meals) {
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  return meals.find((meal) => {
    const start = timeToMinutes(meal.start_time);
    const end = timeToMinutes(meal.end_time);

    return currentMinutes >= start && currentMinutes < end;
  });
}

async function getMealTypes() {
  try {
    const response = await proxy.search(
      "staff.meal",
      [],
      ["name", "start_time", "end_time"],
    );

    mealType.value = response.data ?? [];
  } catch (error) {
    console.error("Error fetching meal types:", error);
  }
}

function adaptMealRecords(log) {
  const date = new Date(log.validation_datetime);
  const adjustedDate = subHours(date, 5);

  return {
    id: Math.random(),
    name: log.employee_name,
    numberId: maskLastFour(log.employee_doc),
    charge: log.employee_position,
    hour: format(adjustedDate, "h:mm aa", { locale: es }),
    image: binaryToBlobUrl(log.employee_photo),
    state: log.status === "success" ? "approved" : "refused",
    description: log.status === "rejected" ? log.description : undefined,
  };
}

function CardItem({ entry, color = "text-green-300", height = "h-34" }) {
  return (
    <div className="rounded-lg border border-gray-900   shadow-xl shadow-white/10 bg-neutral-800/30">
      <div className="flex">
        <div className="md:w-2/6 w-[30%] p-1 overflow-hidden rounded-md">
          <ImagePerfil
            src={
              entry.image ||
              "https://www.shutterstock.com/image-vector/image-icon-600nw-211642900.jpg"
            }
            alt={entry.name}
            className="w-full h-full object-cover cursor-pointer hover:scale-110 transform duration-200 rounded-md"
            height={height}
          />
        </div>

        <div className="md:w-4/6 w-[70%] flex justify-between flex-col pb-2 px-3 pt-2">
          <div className="w-full">
            <h2
              className={cn(
                "font-semibold capitalize truncate text-xs md:text-xl text-center",
                color,
              )}
            >
              {entry.name}
            </h2>

            <div className="flex flex-col w-full text-xl text-gray-100">
              <span className="text-gray-100 font-medium flex items-center space-x-2">
                <span>
                  <UserIcon className="w-4 h-4 fill-current" />
                </span>
                <span className="truncate">{entry.charge}</span>
              </span>

              <span className="text-gray-100 font-medium flex items-center space-x-2">
                <span>
                  <IdentificationIcon className="w-4 h-4 fill-current" />
                </span>
                <span className="truncate">{entry.numberId}</span>
              </span>

              <span className="text-gray-100 font-medium flex items-center space-x-2">
                <TimeIcon className="w-4 h-4 fill-current" />
                <span>{entry.hour}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
      {entry.description && (
        <div className="w-full mt-2 border-t border-gray-200 px-2 py-1.5">
          <p className="font-bold text-gray-100 truncate">
            {entry.description}
          </p>
        </div>
      )}
    </div>
  );
}

function MealStaff() {
  const blobUrls = useRef([]);

  useEffect(() => {
    return () => {
      blobUrls.current.forEach((url) => URL.revokeObjectURL(url));
      blobUrls.current = [];
    };
  }, []);

  useEffect(() => {
    getMealTypes();

    const updateTime = () => {
      const now = new Date();

      currentTime.value = now
        .toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
        .toLowerCase();

      if (mealType.value.length) {
        currentMeal.value = getCurrentMealType(mealType.value);
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  effect(() => {
    if (!mealType.value.length) return;
    currentMeal.value = getCurrentMealType(mealType.value);
  });

  const mealId = currentMeal.value?.id;

  const { data: records = [] } = useQuery({
    queryKey: ["staff-meal", "get_meal_log_info", mealId],
    queryFn: async () => {
      if (!mealId) return [];

      const { data, error } = await proxy.method({
        model: "staff.meal.log",
        method: "get_meal_log_info",
        args: [{ meal_id: mealId }],
      });

      if (error) return [];
      return data ?? [];
    },
    enabled: !!mealId,
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const approved = (records.last_success || []).map(adaptMealRecords);
  const refused = (records.last_rejected || []).map(adaptMealRecords);

  blobUrls.current = [...approved, ...refused]
    .map((r) => r.image)
    .filter((url) => url?.startsWith("blob:"));

  return (
    <div
      className="p-4 w-full bg-neutral-800  -top-2 relative h-[calc(100vh-4rem)] overflow-hidden bg-cover bg-top-right bg-no-repeat"
      style={{ backgroundImage: `url(${imgBackground})` }}
    >
      <style>{`
        #outlet {
          overflow: hidden;
        }
      `}</style>
      <div className="mb-6 flex items-center justify-between text-white">
        <div className="flex text-4xl space-x-2 items-center divide-x divide-gray-300 uppercase">
          <h1 className="pr-4">Control de Alimentación</h1>
          <span className="pl-3 text-amber-500">
            {currentMeal.value?.name || "Fuera de horario"}
          </span>
        </div>

        <div className="flex space-x-3 items-center rounded-xl">
          <Time className="size-10 fill-current" />
          <span className="text-4xl uppercase">{currentTime}</span>
        </div>
      </div>

      {currentMeal.value ? (
        <div className="flex space-x-8">
          <div className="bg-neutral-600/80  border border-gray-700 shadow-md rounded-lg p-4 w-[75%] overflow-hidden h-[82vh] relative">
            <div className="flex space-x-2 items-center mb-3">
              <h2 className="text-4xl text-green-300 up">Aprobados</h2>
              <span className="bg-white rounded-xl text-black min-w-10 px-2 h-10 flex items-center justify-center font-bold text-lg">
                {records.total_success ?? 0}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {approved.slice(0, 15).map((item) => (
                <CardItem key={item.id} entry={item} height="!h-32" />
              ))}
            </div>
            {approved.length > 0 && (
              <div className="h-20 pointer-events-none w-full absolute bottom-0 left-0 bg-gradient-to-t from-neutral-900 from- via-neutral-700/80 via- to-transparent to-" />
            )}
          </div>

          <div className="bg-neutral-600/80 ba border border-gray-700 shadow-md rounded-lg p-4 w-[25%] overflow-hidden h-[82vh] relative">
            <div className="flex space-x-2 items-center justify-center mb-3">
              <h2 className="text-4xl text-rose-300">Denegados</h2>
              <span className="bg-white rounded-xl text-black min-w-10 px-2 h-10 flex items-center justify-center font-bold text-lg">
                {records.total_rejected ?? 0}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4 ">
              {refused.slice(0, 4).map((item) => (
                <CardItem
                  key={item.id}
                  entry={item}
                  color="text-rose-300"
                  height="!h-full"
                />
              ))}
            </div>
            {refused.length > 0 && (
              <div className="h-20 pointer-events-none w-full absolute bottom-0 left-0 bg-gradient-to-t from-neutral-900 from- via-neutral-700/80 via- to-transparent to-" />
            )}
          </div>
        </div>
      ) : (
        <div className="flex space-x-8 h-[80%] content-center items-center justify-center text-3xl">
          <p className="text-white  backdrop-blur-md p-4 rounded-md bg-neutral-800/20">
            No existe ningún horario de comida para esta hora
          </p>
        </div>
      )}
    </div>
  );
}

export default MealStaff;
